import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import deleteIcon from "../../assets/deleteIcon.svg";
import messageIcon from "../../assets/Images/messageIcon.svg";
import VerticalSpace from "../VerticalSpace";
import Label from "../Common/Label";
import RowContainer from "../Common/RowContainer";
import CustomDropdown from "../Common/CustomDropdown";
import NestedMenu from "../ParticlesDropdown/NestedMenu";
import LessonsDropdown from "../LessonDropdwon/lessonsDropdwon";
import DeleteConfirmation from "../Common/DeleteConfirmation";
import "../../stylesheets/theme.css";
import { baseURL, getRequest, postRequest } from "../../api/apiClient";
import Toast from "../Common/Toast";
import RightSideArrow from "../../assets/RightSideArrow.svg";
import RadioButton from "../Common/RadioButton";
import Loader from "../../utils/Loading";
import Tooltip from "../Common/Tooltip";
import gsap from "gsap";
import "../../stylesheets/common.css";
import { DropdownValue, Pair } from "../../types";
import {
  ensureParticleValue1AtEnd,
  ensureParticleValueAtStart,
  getParticleReadOnlyStatus,
  particlePairValues,
} from "../../utils/particlePairUtils";
import { computeTooltipPosition } from "../../utils/tooltipUtils";

interface Theme {
  id: number;
  name: string;
}

interface Lesson {
  id: number;
  name: string;
  themes: Theme[];
}

interface Level {
  level?: string;
  lessons: Lesson[];
}

interface TooltipData {
  id?: number;
  textfieldId?: number;
  type: string;
  originalWord: string;
  tooltipText: string;
}

const TextFields = () => {
  const { id } = useParams();
  const [question, setQuestion] = useState("");
  const [lessonsData, setLessonsData] = useState<Level[]>([]);
  const [level, setLevel] = useState("");
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [textfieldId, setTextFieldId] = useState("");
  const [points, setPoints] = useState("");
  const [nextChallengeId, setNextChallengeId] = useState("");
  const [previousChallengeId, setPreviousChallengeId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({
    question: "",
    lesson: "",
  });
  const [deleteDialogMessage, setDeleteDialogMessage] = useState("");
  const [onConfirmDelete, setOnConfirmDelete] = useState<() => void>(() => { });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pairToDelete, setPairToDelete] = useState<{
    index: number;
    id?: number;
  } | null>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData[]>([]);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [validationError, setValidationError] = useState<{
    pairs: {
      [key: number]: {
        dropdownValue?: string;
        inputValue?: string;
      };
    };
  }>({
    pairs: {},
  });
  const [showTextFieldsDeleteDialog, setShowTextFieldsDeleteDialog] =
    useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const listPage: number = (location.state as any)?.listPage ?? 0;
  const listLimit: number = (location.state as any)?.listLimit ?? 5;
  const listSearch: string = (location.state as any)?.listSearch ?? "";
  const listFilter: string | null = (location.state as any)?.listFilter ?? null;
  const listLevel: string | null = (location.state as any)?.listLevel ?? null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        showTooltip
      ) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTooltip]);

  useEffect(() => {
    fetchTextFieldsData();
  }, [id]);

  const fetchTextFieldsData = async () => {
    try {
      setIsLoading(true);
      let response;
      if (id) {
        // Get parameters from location.state (passed from navigation)
        const stateFilter = (location.state as any)?.listFilter;
        const stateSearch = (location.state as any)?.listSearch;
        const stateLevel = (location.state as any)?.listLevel;
        const statePage = (location.state as any)?.listPage;
        const stateLimit = (location.state as any)?.listLimit;

        // Build query string with state parameters
        const queryParams = new URLSearchParams();
        if (stateFilter && stateFilter !== "All") queryParams.set('filter', stateFilter);
        if (stateSearch) queryParams.set('search', stateSearch);
        if (stateLevel && stateLevel !== "All Levels") queryParams.set('level', stateLevel);
        if (statePage !== undefined) queryParams.set('page', statePage.toString());
        if (stateLimit !== undefined) queryParams.set('limit', stateLimit.toString());
        
        const queryString = queryParams.toString();
        const apiUrl = queryString 
          ? `${baseURL}/textfield-challenge/get-textfield-challenge-by-id/${id}?${queryString}`
          : `${baseURL}/textfield-challenge/get-textfield-challenge-by-id/${id}`;
        
        response = await getRequest(apiUrl);
      } else {
        setIsLoading(false);
        return;
      }

      if (response?.data.success) {
        const updatedTextFieldsData = response.data.data.textFieldsChallenge;
        const particles = response.data.data.particles;
        const tooltips = response.data.data.tooltips;
        const nextChallengeId = response.data.data.nextTextFieldsChallengeId;
        const previousChallengeId =
          response.data.data.previousTextFieldsChallengeId;

        setQuestion(updatedTextFieldsData.question);
        setTooltipData(tooltips);
        const themeName = await fetchThemeById(updatedTextFieldsData.themeId);
        setLessonsData([
          {
            lessons: [
              {
                id: updatedTextFieldsData.themeId,
                name: themeName.name,
                themes: [],
              },
            ],
          },
        ]);
        setLevel(themeName.lessonsLevel);

        setPoints(`${updatedTextFieldsData.points} pts`);

        const fetchedPairs = await Promise.all(
          particles.map(async (item: any, index: number) => {
            const particleData = await fetchParticleById(item.type);

            return {
              id: item.id,
              dropdownValue: {
                id: particleData.id,
                name: particleData.name,
                pId: particleData.pId,
                particleType: particleData.particleType,
                particleValue: particleData.particleValue,
                particleType1: particleData.particleType1,
                particleValue1: particleData.particleValue1,
                children: particleData.children || [],
              },
              inputValue: item.value,
              order: item.order || index + 1,
              isReadOnly: getParticleReadOnlyStatus(particleData),
            };
          })
        );
        setPairs(fetchedPairs);
        setNextChallengeId(nextChallengeId);
        setPreviousChallengeId(previousChallengeId);
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleQuestionChange = (value: string) => {
    setQuestion(value);
    setErrors({
      ...errors,
      question: value ? "" : "Please select a question.",
    });
  };

  const handleSelectItem = (item: Lesson) => {
    setLessonsData([{ lessons: [item] }]);
    setErrors({
      ...errors,
      lesson: item ? "" : "Please select a lesson.",
    });
  };

  const addNewPair = () => {
    const reorderedPairs = pairs
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((pair, index) => ({
        ...pair,
        order: index + 1,
      }));

    const newPair = {
      dropdownValue: null,
      inputValue: "",
      order: reorderedPairs.length + 1,
    };
    setPairs([...reorderedPairs, newPair]);
  };

  const handleDropdownChange = (index: number, item: DropdownValue) => {
    const updatedPairs = [...pairs];

    particlePairValues(item, index, updatedPairs);

    const updatedErrors = { ...validationError };
    if (item.particleValue || item.particleValue1) {
      delete updatedErrors.pairs[index];
    } else if (updatedErrors.pairs[index]) {
      updatedErrors.pairs[index].dropdownValue = "";
    } else {
      updatedErrors.pairs[index] = { dropdownValue: "" };
    }
    setPairs(updatedPairs);
    setValidationError(updatedErrors);
  };

  // const handleInputChange = (index: number, value: string) => {
  //   console.log('Input Value:- ' + value)
  //   const updatedPairs = [...pairs];
  //   const pair = updatedPairs[index];
  //   if (pair.dropdownValue) {
  //     value = ensureParticleValue1AtEnd(
  //       value,
  //       pair.dropdownValue.particleValue1
  //     );
  //   }
  //   updatedPairs[index].inputValue = value;
  //   const updatedErrors = { ...validationError };
  //   if (updatedErrors.pairs[index]) {
  //     updatedErrors.pairs[index].inputValue = "";
  //   } else {
  //     updatedErrors.pairs[index] = { inputValue: "" };
  //   }
  //   setPairs(updatedPairs);
  //   setValidationError(updatedErrors);
  // };

  const handleInputChange = (index: number, value: string) => {
    const updatedPairs = [...pairs];
    const pair = updatedPairs[index];

    // Handle particleValue (black type) - should be at the beginning and fixed
    if (pair.dropdownValue?.particleType === "black" && pair.dropdownValue?.particleValue) {
      value = ensureParticleValueAtStart(
        value,
        pair.dropdownValue.particleValue
      );
    }

    // Handle particleValue1 (other types) - should be at the end
    // BUT skip if particleValue1 is "..." (placeholder for user input)
    if (pair.dropdownValue?.particleValue1 && pair.dropdownValue.particleValue1 !== "...") {
      value = ensureParticleValue1AtEnd(
        value,
        pair.dropdownValue.particleValue1
      );
    }

    updatedPairs[index].inputValue = value;

    const updatedErrors = { ...validationError };
    if (updatedErrors.pairs[index]) {
      updatedErrors.pairs[index].inputValue = "";
    } else {
      updatedErrors.pairs[index] = { inputValue: "" };
    }

    setPairs(updatedPairs);
    setValidationError(updatedErrors);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const pair = pairs[index];
    const input = e.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;

    if (pair.dropdownValue?.particleType === "black" && pair.dropdownValue?.particleValue) {
      const fixedValue = pair.dropdownValue.particleValue.trim();
      const fixedLength = fixedValue.length;

      // Prevent deletion of the fixed particle value at the beginning
      if (
        (e.key === 'Backspace' && cursorPosition <= fixedLength) ||
        (e.key === 'Delete' && cursorPosition < fixedLength) ||
        (e.key === 'ArrowLeft' && cursorPosition <= fixedLength && (e.ctrlKey || e.metaKey)) ||
        (cursorPosition < fixedLength && e.key.length === 1) // Prevent typing within the fixed part
      ) {
        e.preventDefault();
        // Move cursor to the end of fixed value
        setTimeout(() => {
          input.setSelectionRange(fixedLength, fixedLength);
        }, 0);
      }
    }
  };




  const removePair = async (index: number, id?: number) => {
    if (id !== undefined) {
      try {
        const reqData = {
          modelName: "textfieldsChallengeParticles",
          id: id,
        };
        const response = await postRequest(
          `${baseURL}/lesson/delete-bretzel-content`,
          reqData
        );
        if (response?.data) {
          const updatedPairs = [...pairs];
          updatedPairs.splice(index, 1);
          setPairs(updatedPairs);
          Toast(response?.data.statusMessage, "success");
        } else {
          console.error("Error deleting pair:", response?.data);
        }
      } catch (error) {
        console.error("Error deleting pair:", error);
      }
    } else {
      const updatedPairs = [...pairs];
      updatedPairs.splice(index, 1);
      setPairs(updatedPairs);

      const removeErrors = { ...validationError };
      if (removeErrors.pairs) {
        delete removeErrors.pairs[index];
      }
      setValidationError(removeErrors);
    }
  };

  const handleDeletePair = (index: number, id?: number) => {
    if (id !== undefined) {
      setPairToDelete({ index, id });
      setDeleteDialogMessage("Are you sure you want to delete this items?");
      setOnConfirmDelete(() => () => removePair(index, id)); // Pass index and ID to confirm function
      setShowDeleteDialog(true);
    } else {
      removePair(index);
    }
  };

  const handleSave = async () => {
    let hasError = false;

    const validationErrors = {
      question: !question ? "question is required." : "",
      lesson: !lessonsData.length ? "Please select a lesson." : "",
    };

    setErrors(validationErrors);

    if (pairs.length === 0) {
      Toast("Please add at least one pair.", "error");
      return;
    }

    const newErrorsColumn1: {
      [key: number]: { dropdownValue?: string; inputValue?: string };
    } = {};
    pairs.forEach((item, index) => {
      newErrorsColumn1[index] = {};
      if (!item.dropdownValue) {
        newErrorsColumn1[index].dropdownValue = "required";
        hasError = true;
      }
      if (!item.inputValue) {
        newErrorsColumn1[index].inputValue = "required";
        hasError = true;
      }
    });

    setValidationError((prevErrors) => ({
      ...prevErrors,
      pairs: newErrorsColumn1,
    }));

    if (hasError) {
      return;
    }
    if (Object.values(validationErrors).some((error) => error)) {
      return;
    }
    //  if (Object.values(validationError).some((error) => error)) {
    //    return;
    //  }

    const particles = pairs.map((pair) => ({
      id: pair.id,
      value: pair.inputValue,
      type: pair.dropdownValue?.id || "",
      order: pair.order,
    }));

    const firstLesson = lessonsData[0];

    const themeId = firstLesson.lessons[0].id;

    const requestData = {
      question: question,
      themeId: themeId,
      points: 1,
      particles: particles,
    };
    try {
      setIsLoading(true);
      let response;
      const updateId = id || textfieldId;
      if (updateId) {
        response = await postRequest(
          `${baseURL}/textfield-challenge/update-textfield-challenge/${updateId}`,
          requestData
        );
      } else {
        response = await postRequest(
          `${baseURL}/textfield-challenge/create-textfield-challenge`,
          requestData
        );
      }

      if (response?.data.success) {
        const updatedTextFieldsData = response.data.data.challenge;
        const particles = response.data.data.particle;
        setTextFieldId(updatedTextFieldsData.id);
        setQuestion(updatedTextFieldsData.question);
        setPoints(`${updatedTextFieldsData.points} pts`);
        const fetchedPairs = await Promise.all(
          particles.map(async (item: any, index: number) => {
            const particleData = await fetchParticleById(item.type);

            return {
              id: item.id,
              dropdownValue: {
                id: particleData.id,
                name: particleData.name,
                pId: particleData.pId,
                particleType: particleData.particleType,
                particleValue: particleData.particleValue,
                particleType1: particleData.particleType1,
                particleValue1: particleData.particleValue1,
                children: particleData.children || [],
              },
              inputValue: item.value,
              order: item.order || index + 1,
              isReadOnly: getParticleReadOnlyStatus(particleData),
            };
          })
        );
        const sortedPairs = fetchedPairs.sort((a, b) => a.order - b.order);
        setPairs(sortedPairs);
        Toast(response?.data.statusMessage, "success");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const fetchThemeById = async (id: number) => {
    try {
      const response = await getRequest(
        `${baseURL}/notification-challenge/get-themename-by-id/${id}`
      );
      return response?.data.data;
    } catch (error) {
      console.error("Error fetching particle by ID:", error);
      throw error;
    }
  };

  const fetchParticleById = async (id: number) => {
    try {
      const response = await getRequest(
        `${baseURL}/particle/get-particleById/${id}`
      );
      return response?.data.data;
    } catch (error) {
      console.error("Error fetching particle by ID:", error);
      throw error;
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setPairToDelete(null);
  };

  const tooltipAvailable = (selectedText: string) => {
    return tooltipData.some((tooltip) => tooltip.originalWord === selectedText);
  };

  const calculateTooltipPosition = () => {
    if (inputRef.current) {
      const input = inputRef.current;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const selected = input.value.slice(start, end);
      setSelectedText(selected);

      if (start !== end && selected) {
        // measure text width
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d")!;
        const style = window.getComputedStyle(input);
        ctx.font = `${style.fontSize} ${style.fontFamily}`;
        const textBeforeWidth = ctx.measureText(
          input.value.slice(0, start)
        ).width;

        // gather inputs for our helper
        const rect = input.getBoundingClientRect();
        const screenW = window.innerWidth;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        const isWindows = navigator.platform.toLowerCase().includes("win");

        // compute and set
        const { top, left } = computeTooltipPosition(
          rect,
          textBeforeWidth,
          scrollX,
          scrollY,
          screenW,
          isWindows
        );
        setTooltipPosition({ top, left });
        setShowTooltip(false);
      } else {
        setShowTooltip(false);
      }
    }
  };

  const handleTextSelection = () => {
    calculateTooltipPosition();
  };

  const handleIconClick = () => {
    if (!id && !textfieldId) {
      return Toast("Please create a TextField", "error");
    }
    if (selectedText) {
      if (tooltipAvailable(selectedText)) {
        const existingTooltip = tooltipData.find(
          (tooltip) => tooltip.originalWord === selectedText
        );
        setShowTooltip(true);
        setTooltipData(
          tooltipData.map((tooltip) =>
            tooltip.originalWord === selectedText
              ? { ...tooltip, tooltipText: existingTooltip?.tooltipText || "" }
              : tooltip
          )
        );
      } else {
        setShowTooltip(true);
        //  setTooltipData([]);
      }
    } else {
      Toast("Please select any word first", "error");
      setShowTooltip(false);
    }
  };

  const handleSaveMessage = async (
    message: string,
    action: "add" | "update"
  ) => {
    const reqData = {
      challengeId: +(id || textfieldId),
      type: "TextField",
      originalWord: selectedText,
      tooltipText: message,
    };
    if (action === "add") {
      setShowTooltip(false);

      Toast("Tooltip created successfully.", "success");
      const response = await postRequest(
        `${baseURL}/tooltip/create-tooltip`,
        reqData
      );

      if (response?.data) {
        setTooltipData((prevTooltipData) => [
          ...prevTooltipData,
          response?.data.data.tooltip,
        ]);
      }
    } else if (action === "update") {
      const tooltipToUpdate = tooltipData.find(
        (tooltip) => tooltip.originalWord === selectedText
      );

      if (tooltipToUpdate) {
        const updateData = { tooltipText: message };
        setShowTooltip(false);
        Toast("Tooltip updated successfully.", "success");
        const response = await postRequest(
          `${baseURL}/tooltip/update-tooltip/${tooltipToUpdate.id}`,
          updateData
        );

        if (response?.data) {
          setTooltipData((prevTooltipData) =>
            prevTooltipData.map((tooltip) =>
              tooltip.id === tooltipToUpdate.id
                ? { ...tooltip, tooltipText: message }
                : tooltip
            )
          );
        }
      }
    }
  };

  const handleAction = async (action: "close" | "delete") => {
    if (action === "delete") {
      const tooltipToDelete = tooltipData.find(
        (tooltip) => tooltip.originalWord === selectedText
      );

      if (tooltipToDelete) {
        setShowTooltip(false);
        Toast("Tooltip deleted successfully", "success");
        const response = await postRequest(
          `${baseURL}/tooltip/delete-tooltip-by-id/${tooltipToDelete.id}`
        );
        if (response?.data) {
          setTooltipData((prevTooltipData) =>
            prevTooltipData.filter(
              (tooltip) => tooltip.id !== tooltipToDelete.id
            )
          );
        }
      }
    } else if (action === "close") {
      setShowTooltip(false);
      setSelectedText("");
    }
  };

  const handleBackClick = async () => {
    if (previousChallengeId) {
      navigate(`/textfields/edit-textfields/${previousChallengeId}`, {
        state: { listPage, listLimit, listSearch, listFilter, listLevel },
      });
    } else {
      const params = new URLSearchParams({ page: listPage.toString(), limit: listLimit.toString() });
      if (listSearch) params.set("search", listSearch);
      if (listFilter && listFilter !== "All") params.set("filter", listFilter);
      if (listLevel && listLevel !== "All Levels") params.set("level", listLevel);
      navigate(`/textfields?${params.toString()}`);
    }
  };

  const handleCloseClick = () => {
    const params = new URLSearchParams({ page: listPage.toString(), limit: listLimit.toString() });
    if (listSearch) params.set("search", listSearch);
    if (listFilter && listFilter !== "All") params.set("filter", listFilter);
    if (listLevel && listLevel !== "All Levels") params.set("level", listLevel);
    navigate(`/textfields?${params.toString()}`);
  };

  const handleNextClick = async () => {
    if (nextChallengeId)
      navigate(`/textfields/edit-textfields/${nextChallengeId}`, {
        state: { listPage, listLimit, listSearch, listFilter, listLevel },
      });
  };

  const OpenDeleteDailog = () => {
    if (id) {
      setShowTextFieldsDeleteDialog(true);
    }
  };

  const deleteTextFields = async (textfieldIds: number) => {
    try {
      const payload = {
        id: textfieldIds,
      };
      const response = await postRequest(
        `${baseURL}/textfield-challenge/delete-textfield-challenge-by-id`,
        payload
      );

      if (response?.status === 200) {
        Toast("textfields deleted successfully", "success");
      } else {
        Toast("Failed to delete challenge", "error");
      }
    } catch (error) {
      console.error("Error deleting challenge:", error);
    }
  };

  const handleTextFieldDelete = async () => {
    await deleteTextFields(Number(id));
    setShowTextFieldsDeleteDialog(false);
    if (nextChallengeId) {
      navigate(`/textfields/edit-textfields/${nextChallengeId}`, {
        state: { listPage, listLimit, listSearch, listFilter, listLevel },
      });
    } else {
      const params = new URLSearchParams({ page: listPage.toString(), limit: listLimit.toString() });
      if (listSearch) params.set("search", listSearch);
      if (listFilter && listFilter !== "All") params.set("filter", listFilter);
      if (listLevel && listLevel !== "All Levels") params.set("level", listLevel);
      navigate(`/textfields?${params.toString()}`);
    }
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 1,
      ease: "power2.out",
    });
  }, [isLoading]);

  return (
    <div className="container-fluid">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="p-3 main-page">
            <div className="d-flex align-items-center" style={{ position: "relative" }}>
              {/* Left: Previous / Back */}
              <div
                className="d-flex align-items-center go-back"
                onClick={handleBackClick}
                style={{ width: "fit-content" }}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="me-2"
                  style={{
                    backgroundColor: "#D9D9D9",
                    padding: "7px",
                    borderRadius: "8px",
                    color: "#000",
                    cursor: "pointer",
                  }}
                />
                <label
                  style={{
                    color: "#FF9E00",
                    fontSize: "16px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  {previousChallengeId ? "Previous" : "Back"}
                </label>
              </div>

              {/* Right: Next */}
              <div style={{ marginLeft: "auto" }}>
                {nextChallengeId && (
                  <div
                    className="d-flex align-items-center go-back"
                    onClick={handleNextClick}
                    style={{ width: "fit-content" }}
                  >
                    <label
                      style={{
                        color: "#FF9E00",
                        fontSize: "16px",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                    >
                      Next
                    </label>
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="ms-2"
                      style={{
                        backgroundColor: "#D9D9D9",
                        padding: "7px",
                        borderRadius: "8px",
                        color: "#000",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <VerticalSpace height={10} />
            <h4
              className="row-container"
              style={{
                textAlign: "center",
                margin: "auto",
                color: "#7542E4",
                fontWeight: "bold",
              }}
            >
              {id || textfieldId ? "Edit" : "Add"} a challenge
            </h4>
            <VerticalSpace height={30} />
            <div
              className="container user-data"
              style={{
                borderRadius: "30px",
                maxWidth: "1117px",
                position: "relative",
              }}
            >
              <div
                className="col-12  p-4"
                style={{
                  borderRadius: "30px 30px 0px 0px",
                  backgroundColor: "#d9d9d9",
                }}
              >
                {level ? (
                  <RowContainer alignItem="center" justifyContent="end">
                    <RadioButton checked={true} />
                    <Label fontSize="14px" fontWeight={400}>
                      {level}
                    </Label>
                  </RowContainer>
                ) : null}
                <Label
                  fontSize="20px"
                  padding="0px 0px 0px 8px"
                  fontWeight={700}
                >
                  Sentence
                </Label>
                <VerticalSpace height={10} />
                <RowContainer className="textFields-container">
                  <RowContainer alignItem="center">
                    <input
                      type="text"
                      className={`form-control ${errors.question ? "is-invalid" : ""
                        }`}
                      ref={inputRef}
                      id="inputthemes"
                      placeholder=""
                      style={{
                        borderRadius: "10px",
                        height: "40px",
                        fontSize: "14px",
                      }}
                      value={question}
                      onChange={(e) => handleQuestionChange(e.target.value)}
                      onMouseUp={handleTextSelection}
                      onKeyUp={handleTextSelection}
                      onDrag={handleTextSelection}
                      onSelect={handleTextSelection}
                    />
                    <div
                      className="p-0"
                      style={{
                        cursor: "pointer",
                        width: "fit-content",
                      }}
                      onClick={handleIconClick}
                    >
                      <img src={messageIcon} className="user-scale" />
                    </div>
                  </RowContainer>
                  {showTooltip && (
                    <div ref={tooltipRef}>
                      <Tooltip
                        position={tooltipPosition}
                        onSaveMessage={handleSaveMessage}
                        onAction={handleAction}
                        tooltipAvailable={tooltipAvailable(selectedText)}
                        tooltipText={
                          selectedText && tooltipData.length > 0
                            ? tooltipData.find(
                              (tooltip) =>
                                tooltip?.originalWord === selectedText
                            )?.tooltipText || ""
                            : ""
                        }
                      />
                    </div>
                  )}
                  <RowContainer
                    alignItem="center"
                    className="textFields-container"
                  >
                    <div className="col-12" style={{ flex: "unset" }}>
                      <LessonsDropdown
                        data={lessonsData as []}
                        onSelectItem={(item) =>
                          handleSelectItem(item as Lesson)
                        }
                        borderColor={errors.lesson ? "#dc3545" : "#D9D9D9"}
                      />
                    </div>
                    <RowContainer alignItem="center" width="70px">
                      <Label fontSize="18px" fontWeight={400}>
                        1 pts
                      </Label>
                      <img src={RightSideArrow} alt="Arrow" />
                    </RowContainer>
                  </RowContainer>
                </RowContainer>
              </div>
              <div
                className="col-12  p-4"
                style={{
                  borderRadius: "0px 0px 30px 30px",
                  backgroundColor: "#d9d9d980",
                }}
              >
                <div className="col d-flex textFields-content">
                  <div className="col-6">
                    <Label
                      fontSize="20px"
                      padding="0px 0px 0px 8px"
                      fontWeight={700}
                    >
                      Guide
                    </Label>
                  </div>
                  <Label fontSize="20px" fontWeight={700}>
                    Text fields (answers)
                  </Label>
                </div>

                <VerticalSpace height={10} />

                <div
                  className="col d-flex flex-column align-itmes-center sentence-example"
                  style={{ padding: "0px 14px 0px 0px", gap: "16px" }}
                >
                  {pairs.map((pair, index) => (
                    <div
                      key={index}
                      className="row sentence-row"
                      style={{ flexWrap: "unset", alignItems: "center" }}
                    >
                      <div
                        className="col sentence-dropdown"
                        style={{ flex: "1" }}
                      >
                        <NestedMenu
                          data={pair.dropdownValue ? [pair.dropdownValue!] : []}
                          onSelectItem={(value) =>
                            handleDropdownChange(index, value as DropdownValue)
                          }
                          borderColor={
                            validationError.pairs[index]?.dropdownValue
                              ? "#dc3545"
                              : "#D9D9D9"
                          }
                        />
                        <div
                          className="p-0 delete-icon"
                          style={{
                            cursor: "pointer",
                            width: "fit-content",
                          }}
                          onClick={() => handleDeletePair(index, pair.id)}
                        >
                          <img
                            className="user-scale"
                            src={deleteIcon}
                            style={{
                              WebkitUserSelect: "none",
                              userSelect: "none",
                            }}
                          />
                        </div>
                      </div>
                      <div className="col sentence-input">
                        <input
                          type="text"
                          value={pair.inputValue || ""}
                          onChange={(e) =>
                            handleInputChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          className={`form-control ${validationError.pairs[index]?.inputValue
                            ? "is-invalid"
                            : ""
                            }`}
                          placeholder=""
                          readOnly={pair.isReadOnly}
                          style={{
                            borderRadius: "10px",
                            height: "40px",
                            fontSize: "14px",
                            backgroundColor: pair.isReadOnly ? "#ffffff00" : "",
                            border: pair.isReadOnly ? "1px solid #D9D9D9" : "",
                          }}
                        />
                      </div>
                      <div
                        className="p-0 delete-button"
                        style={{
                          cursor: "pointer",
                          width: "fit-content",
                        }}
                        onClick={() => handleDeletePair(index, pair.id)}
                      >
                        <img
                          src={deleteIcon}
                          className="user-scale"
                          style={{
                            WebkitUserSelect: "none",
                            userSelect: "none",
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <VerticalSpace height={16} />
                <RowContainer justifyContent="space-between">
                  <button
                    className="user"
                    type="button"
                    style={{
                      color: "#100F15",
                      background: "none",
                      border: "1px solid #100F15",
                      borderRadius: "10px",
                      padding: "14px 20px",
                      fontSize: "14px",
                      width: "auto",
                    }}
                    onClick={addNewPair}
                  >
                    Add new pair
                  </button>
                  {id && (
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        type="button"
                        className="user"
                        style={{
                          color: "#fff",
                          background: "none",
                          backgroundColor: "#7542E4",
                          border: "none",
                          borderRadius: "10px",
                          padding: "14px 40px",
                          fontSize: "14px",
                          width: "auto",
                        }}
                        onClick={handleCloseClick}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="user"
                        style={{
                          color: "#fff",
                          background: "none",
                          backgroundColor: "#EB002C",
                          border: "none",
                          borderRadius: "10px",
                          padding: "14px 40px",
                          fontSize: "14px",
                          width: "auto",
                        }}
                        onClick={OpenDeleteDailog}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </RowContainer>
              </div>
            </div>
          </div>

          <VerticalSpace height={220} />
          <div
            className="container row d-flex justify-content-center publish-button"
            style={{
              position: "fixed",
              zIndex: 1,
              height: "12%",
              maxWidth: "100%",
              bottom: "0px",
              background:
                "linear-gradient(178.35deg, rgba(255, 255, 255, 0) 1.4%, rgba(255, 255, 255, 0.69) 26.79%, #FFFFFF 50.22%)",
            }}
          >
            <div
              className="d-flex justify-content-center"
              style={{
                position: "fixed",
                zIndex: 1,
                bottom: "34px",
              }}
            >
              <button
                type="button"
                className="btn btn-success d-flex justify-content-center user"
                style={{
                  backgroundColor: "#FF9E00",
                  color: "#100F15",
                  height: "fit-content",
                  borderRadius: "10px",
                  padding: "10px 20%",
                  width: "400px",
                  fontSize: "14px",
                }}
                onClick={handleSave}
              >
                Publish
              </button>
            </div>
          </div>
        </>
      )}
      {showDeleteDialog && (
        <DeleteConfirmation
          showDeletePopup={true}
          setShowDeletePopup={setShowDeleteDialog}
          message={deleteDialogMessage}
          deleteButtonLabel="Delete"
          onDelete={onConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
      {showTextFieldsDeleteDialog && (
        <DeleteConfirmation
          showDeletePopup={true}
          setShowDeletePopup={setShowTextFieldsDeleteDialog}
          message="Are you sure you want to delete this textfield?"
          deleteButtonLabel="Delete"
          onDelete={handleTextFieldDelete}
        />
      )}
    </div>
  );
};
export default TextFields;
